/**
 * The groups of products as tiles over the store's listing: a picture, the group's name and how many products it holds, one chosen at a time (tap it again for all).
 * The choice is the shop's own: it is written in the address as field_filters.item_group, the shape the shop's listing already reads, and the listing is asked to draw again.
 */

import { el } from '../dom.js';

const METHOD = 'raftor_websitehq.website_hq.group_tiles.tiles';
const PILLS = 'raftor_websitehq.website_hq.group_tiles.pills';

function readFilters() {
	try {
		const parsed = JSON.parse(new URLSearchParams(window.location.search).get('field_filters') || '{}');
		return parsed && typeof parsed === 'object' ? parsed : {};
	} catch (err) {
		return {};
	}
}

function choose(value) {
	const url = new URL(window.location.href);
	const filters = readFilters();
	const was = url.searchParams.get('pill_field');  // a pill belongs to the group it was chosen in
	if (was) delete filters[was];
	url.searchParams.delete('pill'); url.searchParams.delete('pill_field');
	if (value) filters.item_group = [value]; else delete filters.item_group;
	if (Object.keys(filters).length) url.searchParams.set('field_filters', JSON.stringify(filters)); else url.searchParams.delete('field_filters');
	url.searchParams.delete('start');
	window.history.pushState('filters', '', url.pathname + (url.search || ''));
	const view = window.__raftorProductView;
	if (view && typeof view.make === 'function') view.make(true);
}

function choosePill(field, value, label) {
	const url = new URL(window.location.href);
	const filters = readFilters();
	if (value) filters[field] = [value]; else delete filters[field];
	if (Object.keys(filters).length) url.searchParams.set('field_filters', JSON.stringify(filters)); else url.searchParams.delete('field_filters');
	if (value) { url.searchParams.set('pill', label); url.searchParams.set('pill_field', field); } else { url.searchParams.delete('pill'); url.searchParams.delete('pill_field'); }
	url.searchParams.delete('start');
	window.history.pushState('filters', '', url.pathname + (url.search || ''));
	const view = window.__raftorProductView;
	if (view && typeof view.make === 'function') view.make(true);
}

// The pills an app offers for the chosen group, under the tiles: one chosen at a time, tap it again for all of the group.
function drawPills(box) {
	const group = toArray(readFilters().item_group)[0] || '';
	box.replaceChildren();
	if (!group || typeof frappe === 'undefined' || !frappe.call) return;
	frappe.call({
		method: PILLS,
		args: { group },
		callback: (r) => {
			const spec = (r && r.message) || {};
			if (!spec.field || !(spec.options || []).length || (toArray(readFilters().item_group)[0] || '') !== group) return;
			const chosen = new URLSearchParams(window.location.search).get('pill') || '';
			const chip = (o) => el('button', { type: 'button', class: 'rfhq-chip', dataset: { value: o.value, label: o.label }, 'aria-pressed': String(chosen === o.label) },
				[o.label, el('span', { class: 'rfhq-chip-count', text: String(o.count) })]);
			const strip = el('div', { class: 'rfhq-chips', role: 'group', 'aria-label': spec.label || '' }, spec.options.map(chip));
			strip.addEventListener('click', (e) => {
				const button = e.target.closest('.rfhq-chip');
				if (!button) return;
				const same = button.dataset.label === chosen;
				choosePill(spec.field, same ? '' : button.dataset.value, same ? '' : button.dataset.label);
				drawPills(box);
			});
			box.replaceChildren(...(spec.label ? [el('div', { class: 'rfhq-chips-heading', role: 'heading', 'aria-level': '2', text: spec.label })] : []), strip);
		},
	});
}

function draw(host, rows, onChange) {
	const current = toArray(readFilters().item_group)[0] || '';
	const tile = (row) => {
		const picture = el('span', { class: 'rfhq-tab__mark rfhq-tab__mark--pic' }, [row.image
			? el('img', { class: 'rfhq-tab__pic', src: encodeURI(row.image), alt: '', loading: 'lazy' })
			: el('span', { class: 'rfhq-tab__pic rfhq-tab__pic--blank', text: (row.label || '').slice(0, 1) })]);
		return el('button', { type: 'button', role: 'tab', class: 'rfhq-tab', dataset: { value: row.value }, 'aria-selected': String(current === row.value) },
			[picture, el('span', { class: 'rfhq-tab__label', text: row.label }), el('span', { class: 'rfhq-tab__count', text: String(row.count) })]);
	};
	const strip = el('div', { class: 'rfhq-tabs rfhq-tabs--tiles rfhq-tabs--pictures', role: 'tablist', 'aria-label': __('Groups') }, rows.map(tile));
	strip.addEventListener('click', (e) => {
		const button = e.target.closest('.rfhq-tab');
		if (!button) return;
		choose(button.dataset.value === current ? '' : button.dataset.value);
		draw(host, rows, onChange);
		if (onChange) onChange();
	});
	const pills = el('div', { class: 'rfhq-group-pills rfhq-filter-block rfhq-filter-block--chips' });
	host.replaceChildren(strip, pills);
	drawPills(pills);
}

function toArray(value) {
	if (value == null) return [];
	return Array.isArray(value) ? value : [value];
}

/** Puts the tiles in `host` (an empty element above the listing). `onChange` is called after a choice, so the filter panel can show it too. Nothing is drawn when the site has no groups with products. */
export function mountGroupTiles(host, onChange) {
	if (typeof frappe === 'undefined' || !frappe.call) return;
	frappe.call({
		method: METHOD,
		callback: (r) => {
			const rows = (r && r.message) || [];
			if (rows.length > 1) draw(host, rows, onChange);  // one group alone would be no choice
		},
	});
}
